import React from 'react';
import { motion } from 'framer-motion';
import { ShieldIcon, ActivityIcon, ZapIcon, HeadphonesIcon, AwardIcon } from 'lucide-react';
export const SecuritySection = () => {
  const securityFeatures = [{
    title: 'Reliable and stable',
    description: 'Pigeon offers enterprise-grade reliability, backed by a 99.9% uptime guarantee.',
    icon: <ActivityIcon className="h-6 w-6" data-id="element-588" />,
    badge: '99.9% UPTIME'
  }, {
    title: 'Onboarding and Implementation',
    description: "Get up and running fast — we'll help set up your workflows, train your team, and provide you with helpful resources.",
    icon: <ZapIcon className="h-6 w-6" data-id="element-589" />,
    badge: 'RAPID SETUP'
  }, {
    title: 'Custom and Technical Support',
    description: 'We provide white-glove technical service and 24/7 dedicated customer support, with an average response time of under 20 minutes.',
    icon: <HeadphonesIcon className="h-6 w-6" data-id="element-590" />,
    badge: '24/7 SUPPORT'
  }];
  const certifications = [{
    name: 'SOC 2 Type II',
    description: 'Annual third-party audit of security, availability & privacy controls',
    logo: 'https://www.pigeonimmigration.com/_assets/v11/9a444ee7f8b58e2bc9449b329edc71e81703f7be.png'
  }, {
    name: 'GDPR',
    description: 'Privacy-by-design platform compliant with EU data rights',
    logo: 'https://www.pigeonimmigration.com/_assets/v11/617aa0997cd332dd60d38c152aba0ecfdf84863e.png'
  }, {
    name: 'HIPAA',
    description: 'End-to-end protection for U.S. healthcare data (PHI)',
    logo: 'https://www.pigeonimmigration.com/_assets/v11/6c32fadc695ec83f70310bec49aea03825f769ba.png'
  }];
  return <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#F8FAFF] text-[#0B1220]" data-id="element-591">
      <div className="max-w-7xl mx-auto" data-id="element-592">
        {/* Security Icons */}
        <div className="flex justify-center items-center gap-4 mb-6" data-id="element-593">
          <ShieldIcon className="h-8 w-8 text-[#2E6BFF]" data-id="element-594" />
          <div className="text-[#2E6BFF] font-bold text-2xl" data-id="element-595">|</div>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#0BB489]" data-id="element-596">
            <path d="M19 11H5C3.89543 11 3 11.8954 3 13V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V13C21 11.8954 20.1046 11 19 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-597" />
            <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-598" />
          </svg>
        </div>
        {/* Main Title */}
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="text-4xl sm:text-5xl font-bold text-center mb-16 bg-gradient-to-r from-[#0B1220] to-[#2E6BFF] bg-clip-text text-transparent leading-normal py-1" data-id="element-599">
          Enterprise-grade security & compliance
        </motion.h2>
        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-600">
          {securityFeatures.map((feature, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="bg-white rounded-lg p-8 border border-[#E7EAF0] shadow-lg hover:shadow-xl transition-shadow duration-300" data-id="element-601">
              <div className="flex justify-between items-start mb-6" data-id="element-602">
                <div className="bg-[#F0F5FF] p-4 rounded-lg text-[#2E6BFF]" data-id="element-603">
                  {feature.icon}
                </div>
                <div className="bg-[#E7FFF7] px-3 py-1 rounded text-xs font-medium text-[#0BB489]" data-id="element-604">
                  {feature.badge}
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#0B1220]" data-id="element-605">
                {feature.title}
              </h3>
              <p className="text-[#5A6376] leading-relaxed" data-id="element-606">
                {feature.description}
              </p>
            </motion.div>)}
        </div>
        {/* Compliance Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        delay: 0.2
      }} className="bg-white rounded-lg p-8 border border-[#E7EAF0] shadow-lg" data-id="element-607">
          <div className="flex items-center gap-3 mb-8" data-id="element-608">
            <AwardIcon className="h-6 w-6 text-[#2E6BFF]" data-id="element-609" />
            <h3 className="text-2xl font-bold text-[#0B1220]" data-id="element-610">
              Compliance Certifications
            </h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-611">
            {certifications.map((cert, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: 0.3 + index * 0.1
          }} className="bg-[#F0F5FF] rounded-lg p-6 text-center border border-[#E7EAF0]/50" data-id="element-612">
                <div className="flex justify-center mb-4" data-id="element-613">
                  <img src={cert.logo} alt={`${cert.name} certification`} className="w-16 h-16 rounded-full shadow-md" data-id="element-614" />
                </div>
                <h4 className="text-lg font-semibold mb-2 text-[#0B1220]" data-id="element-615">
                  {cert.name}
                </h4>
                <p className="text-sm text-[#5A6376]" data-id="element-616">{cert.description}</p>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
};